import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { RecommendationChartBar } from '@/services/inventoryService';
import {
  categoryAccentColor,
  categoryBarSegmentColors,
  compareRecommendationBarsByColor,
  translateCategoryLabel,
} from './recommendationCatalog';

export type RecommendationChartFilter = {
  /** `null` = filtro global solo por segmento (Fabbisogno / Scorta). */
  categoryKey: string | null;
  /** Con categoría: `null` = toda la barra. Sin categoría: segmento activo. */
  onDemand: boolean | null;
} | null;

export type RecommendationScopeFilterChip = {
  id: string;
  label: string;
  /** Color de acento (p. ej. salud de inventario). */
  accentColor?: string;
};

type ChartRow = RecommendationChartBar & {
  label: string;
  shortLabel: string;
  pctOnDemand: number;
  pctStock: number;
  pctOfTotal: number;
  visual_on_demand_count: number;
  visual_stock_count: number;
};

interface Props {
  bars: RecommendationChartBar[];
  loading?: boolean;
  activeFilter: RecommendationChartFilter;
  scopeFilterChips?: RecommendationScopeFilterChip[];
  onSegmentClick: (categoryKey: string, onDemand: boolean) => void;
  onCategoryClick: (categoryKey: string) => void;
  onGlobalSegmentClick: (onDemand: boolean) => void;
  onClearFilter?: () => void;
}

const BAR_COLUMN_WIDTH = 52;
const CHART_HEIGHT = 280;
const CHART_MIN_WIDTH = 480;
const MIN_BAR_PIXELS = 12;
const SIDEBAR_WIDTH = '11.5rem';

function detectSqrtScale(rows: { total_count?: number }[], totalRefs: number): boolean {
  if (rows.length < 2 || totalRefs <= 0) return false;
  const totals = rows.map((r) => r.total_count || 0).sort((a, b) => b - a);
  const [max, second = 0] = totals;
  if (max / totalRefs > 0.4) return true;
  if (second > 0 && max / second >= 3) return true;
  return false;
}

function toVisualCount(value: number, useSqrt: boolean): number {
  if (value <= 0) return 0;
  return useSqrt ? Math.sqrt(value) : value;
}

function truncateLabel(label: string, max = 14): string {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

function ScopeFilterChips({
  chips,
  activeFilter,
  onClearFilter,
}: {
  chips: RecommendationScopeFilterChip[];
  activeFilter: RecommendationChartFilter;
  onClearFilter?: () => void;
}) {
  const { t } = useTranslation();
  const hasChips = chips.length > 0 || activeFilter;

  if (!hasChips) return null;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200/80 bg-slate-50/90 px-3 py-2">
      <span className="text-[11px] font-medium text-slate-600">
        {t('inventoryPolicies.recommendations.chart.activeScopeFilters')}
      </span>
      {chips.map((chip) => (
        <span
          key={chip.id}
          className="inline-flex max-w-[14rem] items-center gap-1.5 truncate rounded-full border border-white/80 bg-white px-2.5 py-0.5 text-[11px] font-medium text-gray-800 shadow-sm"
          title={chip.label}
        >
          {chip.accentColor ? (
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: chip.accentColor }}
              aria-hidden
            />
          ) : null}
          <span className="truncate">{chip.label}</span>
        </span>
      ))}
      {activeFilter ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-[#1681ba]/30 bg-[#1681ba]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#0e6a9a]">
          {activeFilter.categoryKey ? (
            translateCategoryLabel(t, activeFilter.categoryKey)
          ) : (
            t('inventoryPolicies.recommendations.chart.globalSegmentFilter')
          )}
          {activeFilter.onDemand !== null ? (
            <>
              {activeFilter.categoryKey ? (
                <span className="font-normal text-[#1681ba]/80">·</span>
              ) : null}
              {activeFilter.onDemand
                ? t('inventoryPolicies.recommendations.chart.onDemand')
                : t('inventoryPolicies.recommendations.chart.stock')}
            </>
          ) : activeFilter.categoryKey ? (
            <span className="font-normal text-[#1681ba]/80">
              {' '}
              ({t('inventoryPolicies.recommendations.chart.wholeCategory')})
            </span>
          ) : null}
          {onClearFilter ? (
            <button
              type="button"
              onClick={onClearFilter}
              className="ml-0.5 rounded p-0.5 hover:bg-[#1681ba]/15"
              aria-label={t('inventoryPolicies.recommendations.chart.clearFilter')}
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

function rowIsDimmed(
  row: ChartRow,
  activeFilter: RecommendationChartFilter,
  segment: 'on_demand' | 'stock',
): boolean {
  if (!activeFilter) return false;
  if (activeFilter.categoryKey === null && activeFilter.onDemand !== null) {
    if (segment === 'on_demand' && !activeFilter.onDemand) return true;
    if (segment === 'stock' && activeFilter.onDemand) return true;
    return false;
  }
  if (activeFilter.categoryKey !== row.category_key) return true;
  if (activeFilter.onDemand === null) return false;
  if (segment === 'on_demand' && !activeFilter.onDemand) return true;
  if (segment === 'stock' && activeFilter.onDemand) return true;
  return false;
}

function GlobalSegmentToggle({
  activeFilter,
  onGlobalSegmentClick,
}: {
  activeFilter: RecommendationChartFilter;
  onGlobalSegmentClick: (onDemand: boolean) => void;
}) {
  const { t } = useTranslation();
  const odActive = activeFilter?.categoryKey === null && activeFilter.onDemand === true;
  const scActive = activeFilter?.categoryKey === null && activeFilter.onDemand === false;

  return (
    <div
      className="flex flex-col items-end gap-1"
      role="group"
      aria-label={t('inventoryPolicies.recommendations.chart.globalSegmentAria')}
    >
      <span className="text-[10px] font-medium text-gray-500">
        {t('inventoryPolicies.recommendations.chart.globalSegmentLabel')}
      </span>
      <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
        <button
          type="button"
          onClick={() => onGlobalSegmentClick(true)}
          className={`rounded px-2.5 py-1 text-[11px] font-semibold transition-colors ${
            odActive
              ? 'bg-[#1681ba] text-white shadow-sm'
              : 'text-gray-700 hover:bg-white'
          }`}
        >
          {t('inventoryPolicies.recommendations.chart.onDemand')}
        </button>
        <button
          type="button"
          onClick={() => onGlobalSegmentClick(false)}
          className={`rounded px-2.5 py-1 text-[11px] font-semibold transition-colors ${
            scActive
              ? 'bg-[#1681ba] text-white shadow-sm'
              : 'text-gray-700 hover:bg-white'
          }`}
        >
          {t('inventoryPolicies.recommendations.chart.stock')}
        </button>
      </div>
    </div>
  );
}

function CategorySidebar({
  rows,
  activeFilter,
  onSegmentClick,
  onCategoryClick,
  onClearFilter,
}: {
  rows: ChartRow[];
  activeFilter: RecommendationChartFilter;
  onSegmentClick: (categoryKey: string, onDemand: boolean) => void;
  onCategoryClick: (categoryKey: string) => void;
  onClearFilter?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <aside
      className="flex shrink-0 flex-col rounded-md border border-gray-200 bg-gray-50/80 lg:w-[var(--rec-sidebar-w)]"
      style={{ '--rec-sidebar-w': SIDEBAR_WIDTH } as CSSProperties}
    >
      <div className="flex items-center justify-between gap-2 border-b border-gray-200/80 px-2.5 py-2">
        <p className="text-[11px] font-semibold text-gray-700">
          {t('inventoryPolicies.recommendations.chart.quickSelect')}
        </p>
        {activeFilter && onClearFilter ? (
          <button
            type="button"
            onClick={onClearFilter}
            className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium text-[#0e6a9a] hover:bg-[#1681ba]/10"
            aria-label={t('inventoryPolicies.recommendations.chart.clearFilter')}
          >
            <X className="h-3 w-3" />
            {t('inventoryPolicies.recommendations.chart.clearFilterShort')}
          </button>
        ) : null}
      </div>

      <ul
        className="max-h-[17.5rem] space-y-0.5 overflow-y-auto p-1.5 lg:max-h-[var(--rec-chart-h)]"
        style={{ '--rec-chart-h': `${CHART_HEIGHT}px` } as CSSProperties}
      >
        {rows.map((row) => {
          const segColors = categoryBarSegmentColors(row.color_variant);
          const accent = categoryAccentColor(row.color_variant);
          const isCategoryActive = activeFilter?.categoryKey === row.category_key;
          const wholeCategoryActive = isCategoryActive && activeFilter?.onDemand === null;
          const odActive = isCategoryActive && activeFilter?.onDemand === true;
          const scActive = isCategoryActive && activeFilter?.onDemand === false;
          const od = row.on_demand_count || 0;
          const sc = row.stock_count || 0;
          const categoryTitle = `${row.label} · ${t('inventoryPolicies.recommendations.chart.wholeCategoryHint')}`;

          return (
            <li key={row.category_key}>
              <div
                className={`rounded-md px-2 py-1.5 transition-colors ${
                  isCategoryActive
                    ? 'bg-white shadow-sm ring-1 ring-[#1681ba]/25'
                    : 'hover:bg-white/80'
                }`}
              >
                <button
                  type="button"
                  disabled={(row.total_count || 0) === 0}
                  onClick={() => onCategoryClick(row.category_key)}
                  className={`mb-1 flex w-full items-start gap-1.5 rounded px-0.5 py-0.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
                    wholeCategoryActive ? 'bg-[#1681ba]/8' : 'hover:bg-gray-100/80'
                  }`}
                  title={categoryTitle}
                >
                  <span
                    className="mt-1 h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: accent }}
                    aria-hidden
                  />
                  <span
                    className="min-w-0 flex-1 text-[11px] font-medium leading-snug text-gray-800"
                  >
                    {row.label}
                  </span>
                  <span className="shrink-0 text-right text-[10px] font-semibold tabular-nums text-gray-500">
                    <span className="block">{(row.total_count || 0).toLocaleString()}</span>
                    <span className="block text-[9px] font-medium text-gray-400">
                      {t('inventoryPolicies.recommendations.chart.barShareOfTotal', {
                        pct: row.pctOfTotal,
                      })}
                    </span>
                  </span>
                </button>
                <div className="grid grid-cols-2 gap-1 pl-3.5">
                  <button
                    type="button"
                    disabled={od === 0}
                    onClick={() => onSegmentClick(row.category_key, true)}
                    className={`rounded px-1 py-0.5 text-[10px] font-semibold tabular-nums transition-all disabled:cursor-not-allowed disabled:opacity-35 ${
                      odActive
                        ? 'text-white shadow-sm ring-1 ring-gray-900/15'
                        : 'text-white hover:brightness-95'
                    }`}
                    style={{ backgroundColor: segColors.onDemandStrong }}
                    title={`${row.label} · ${t('inventoryPolicies.recommendations.chart.onDemand')}`}
                  >
                    {t('inventoryPolicies.recommendations.chart.segmentShortOnDemand')}{' '}
                    {od.toLocaleString()}
                  </button>
                  <button
                    type="button"
                    disabled={sc === 0}
                    onClick={() => onSegmentClick(row.category_key, false)}
                    className={`rounded px-1 py-0.5 text-[10px] font-semibold tabular-nums transition-all disabled:cursor-not-allowed disabled:opacity-35 ${
                      scActive
                        ? 'text-gray-900 shadow-sm ring-1 ring-gray-900/15'
                        : 'text-gray-800 hover:brightness-95'
                    }`}
                    style={{ backgroundColor: segColors.stockLight }}
                    title={`${row.label} · ${t('inventoryPolicies.recommendations.chart.stock')}`}
                  >
                    {t('inventoryPolicies.recommendations.chart.segmentShortStock')}{' '}
                    {sc.toLocaleString()}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

export function InventoryRecommendationChart({
  bars,
  loading = false,
  activeFilter,
  scopeFilterChips = [],
  onSegmentClick,
  onCategoryClick,
  onGlobalSegmentClick,
  onClearFilter,
}: Props) {
  const { t } = useTranslation();

  const totalRefs = useMemo(
    () => bars.reduce((sum, row) => sum + (row.total_count || 0), 0),
    [bars],
  );

  const baseChartData = useMemo(
    () =>
      [...bars].sort(compareRecommendationBarsByColor).map((bar) => {
        const total = bar.total_count || 0;
        const od = bar.on_demand_count || 0;
        const sc = bar.stock_count || 0;
        const label = translateCategoryLabel(t, bar.category_key);
        return {
          ...bar,
          label,
          shortLabel: truncateLabel(label),
          pctOnDemand: total > 0 ? Math.round((od / total) * 100) : 0,
          pctStock: total > 0 ? Math.round((sc / total) * 100) : 0,
          pctOfTotal: totalRefs > 0 ? Math.round((total / totalRefs) * 100) : 0,
        };
      }),
    [bars, t, totalRefs],
  );

  const useSqrtScale = useMemo(
    () => detectSqrtScale(baseChartData, totalRefs),
    [baseChartData, totalRefs],
  );

  const chartData = useMemo<ChartRow[]>(
    () =>
      baseChartData.map((row) => ({
        ...row,
        visual_on_demand_count: toVisualCount(row.on_demand_count || 0, useSqrtScale),
        visual_stock_count: toVisualCount(row.stock_count || 0, useSqrtScale),
      })),
    [baseChartData, useSqrtScale],
  );

  const innerChartWidth = Math.max(CHART_MIN_WIDTH, chartData.length * BAR_COLUMN_WIDTH);

  const yMax = useMemo(() => {
    const peak = Math.max(
      ...chartData.map((r) => r.visual_on_demand_count + r.visual_stock_count),
      0,
    );
    return Math.max(peak + Math.ceil(peak * 0.1), 2);
  }, [chartData]);

  const formatAxisTick = (value: number) => {
    const raw = useSqrtScale ? value * value : value;
    return Math.round(raw).toLocaleString();
  };

  const renderXAxisTick = (props: { x?: number; y?: number; payload?: { value: string } }) => {
    const { x = 0, y = 0, payload } = props;
    const short = payload?.value ?? '';
    const row = chartData.find((r) => r.shortLabel === short);
    const accent = row ? categoryAccentColor(row.color_variant) : '#9CA3AF';
    const isActive = row && activeFilter?.categoryKey === row.category_key;

    return (
      <g transform={`translate(${x},${y})`} style={{ cursor: row ? 'pointer' : 'default' }}>
        {row ? (
          <title>{`${row.label} · ${t('inventoryPolicies.recommendations.chart.wholeCategoryHint')}`}</title>
        ) : null}
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill={isActive ? '#0e6a9a' : '#374151'}
          fontSize={10}
          fontWeight={isActive ? 600 : 400}
          transform="rotate(-42)"
          onClick={() => {
            if (row) onCategoryClick(row.category_key);
          }}
        >
          {short}
        </text>
        <rect
          x={-3}
          y={14}
          width={6}
          height={6}
          rx={1}
          fill={accent}
          transform="rotate(-42)"
          onClick={() => {
            if (row) onCategoryClick(row.category_key);
          }}
        />
      </g>
    );
  };

  const showInitialSpinner = loading && bars.length === 0;
  const isRefreshing = loading && bars.length > 0;

  if (showInitialSpinner) {
    return (
      <div className="flex min-h-[14rem] items-center justify-center rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#1681ba]" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-medium text-gray-900">
              {t('inventoryPolicies.recommendations.chart.title')}
            </h2>
            {isRefreshing ? (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500"
                aria-live="polite"
                aria-label={t('inventoryPolicies.recommendations.chart.updatingAria')}
              >
                <span
                  className="h-3 w-3 animate-spin rounded-full border-2 border-gray-200 border-t-[#1681ba]"
                  aria-hidden
                />
                {t('inventoryPolicies.recommendations.chart.updating')}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            {t('inventoryPolicies.recommendations.chart.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <GlobalSegmentToggle
            activeFilter={activeFilter}
            onGlobalSegmentClick={onGlobalSegmentClick}
          />
          <p className="text-[10px] text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex h-2.5 w-5 overflow-hidden rounded-sm border border-gray-200">
                <span className="w-1/2 bg-red-500" />
                <span className="w-1/2 bg-red-200" />
              </span>
              {t('inventoryPolicies.recommendations.chart.legendStrong')}
              <span className="text-gray-300">·</span>
              {t('inventoryPolicies.recommendations.chart.legendLight')}
            </span>
          </p>
        </div>
      </div>

      <ScopeFilterChips
        chips={scopeFilterChips}
        activeFilter={activeFilter}
        onClearFilter={onClearFilter}
      />

      <div
        className={
          isRefreshing
            ? 'pointer-events-none opacity-55 transition-opacity duration-200'
            : 'transition-opacity duration-200'
        }
      >
      {chartData.length === 0 ? (
        <div className="flex min-h-[12rem] flex-col items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50/80 px-4 py-10 text-center">
          <p className="max-w-md text-sm text-gray-600">
            {t('inventoryPolicies.recommendations.chart.empty')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
          <div className="min-w-0 flex-1 overflow-x-auto rounded-md border border-gray-100 bg-gradient-to-b from-gray-50/30 to-white">
            <div style={{ width: innerChartWidth, height: CHART_HEIGHT, minWidth: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 16, right: 8, left: 0, bottom: 56 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis
                    dataKey="shortLabel"
                    tick={renderXAxisTick}
                    interval={0}
                    height={56}
                    axisLine={{ stroke: '#d1d5db' }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={useSqrtScale}
                    domain={[0, yMax]}
                    tickFormatter={formatAxisTick}
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#d1d5db' }}
                    width={40}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(22, 129, 186, 0.06)' }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0].payload as ChartRow;
                      const segColors = categoryBarSegmentColors(row.color_variant);
                      return (
                        <div className="max-w-xs rounded-md border border-gray-200 bg-white px-3 py-2.5 text-left text-xs shadow-lg">
                          <p className="font-semibold text-gray-900">{row.label}</p>
                          <p className="mt-1 tabular-nums text-gray-600">
                            {t('inventoryPolicies.recommendations.chart.totalLine', {
                              count: row.total_count,
                            })}
                            {' · '}
                            {t('inventoryPolicies.recommendations.chart.barShareOfTotal', {
                              pct: row.pctOfTotal,
                            })}
                          </p>
                          <div className="mt-2 space-y-1">
                            <p className="flex items-center gap-1.5 text-gray-700">
                              <span
                                className="h-2 w-2 shrink-0 rounded-sm"
                                style={{ backgroundColor: segColors.onDemandStrong }}
                              />
                              {t('inventoryPolicies.recommendations.chart.onDemand')}:{' '}
                              <span className="font-medium tabular-nums">
                                {row.on_demand_count} ({row.pctOnDemand}%)
                              </span>
                            </p>
                            <p className="flex items-center gap-1.5 text-gray-700">
                              <span
                                className="h-2 w-2 shrink-0 rounded-sm"
                                style={{ backgroundColor: segColors.stockLight }}
                              />
                              {t('inventoryPolicies.recommendations.chart.stock')}:{' '}
                              <span className="font-medium tabular-nums">
                                {row.stock_count} ({row.pctStock}%)
                              </span>
                            </p>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="visual_on_demand_count"
                    stackId="rec"
                    barSize={28}
                    minPointSize={MIN_BAR_PIXELS}
                    style={{ cursor: 'pointer' }}
                    onClick={(_data, index) => {
                      const row = chartData[index];
                      if (row) onSegmentClick(row.category_key, true);
                    }}
                  >
                    {chartData.map((row) => {
                      const globalOd =
                        activeFilter?.categoryKey === null && activeFilter.onDemand === true;
                      const isActive =
                        globalOd ||
                        (activeFilter?.categoryKey === row.category_key &&
                          (activeFilter.onDemand === true || activeFilter.onDemand === null));
                      const dimmed = rowIsDimmed(row, activeFilter, 'on_demand');
                      const { onDemandStrong } = categoryBarSegmentColors(row.color_variant);
                      return (
                        <Cell
                          key={`od-${row.category_key}`}
                          fill={onDemandStrong}
                          fillOpacity={dimmed ? 0.25 : 1}
                          stroke={isActive ? '#1f2937' : 'transparent'}
                          strokeWidth={isActive ? 2 : 0}
                        />
                      );
                    })}
                  </Bar>
                  <Bar
                    dataKey="visual_stock_count"
                    stackId="rec"
                    radius={[3, 3, 0, 0]}
                    barSize={28}
                    minPointSize={MIN_BAR_PIXELS}
                    style={{ cursor: 'pointer' }}
                    onClick={(_data, index) => {
                      const row = chartData[index];
                      if (row) onSegmentClick(row.category_key, false);
                    }}
                  >
                    {chartData.map((row) => {
                      const globalSc =
                        activeFilter?.categoryKey === null && activeFilter.onDemand === false;
                      const isActive =
                        globalSc ||
                        (activeFilter?.categoryKey === row.category_key &&
                          (activeFilter.onDemand === false || activeFilter.onDemand === null));
                      const dimmed = rowIsDimmed(row, activeFilter, 'stock');
                      const { stockLight } = categoryBarSegmentColors(row.color_variant);
                      return (
                        <Cell
                          key={`sc-${row.category_key}`}
                          fill={stockLight}
                          fillOpacity={dimmed ? 0.25 : 1}
                          stroke={isActive ? '#1f2937' : 'transparent'}
                          strokeWidth={isActive ? 2 : 0}
                        />
                      );
                    })}
                    <LabelList
                      dataKey="pctOfTotal"
                      position="top"
                      content={(props) => {
                        const { x, y, width, value, index } = props as {
                          x?: number;
                          y?: number;
                          width?: number;
                          value?: number;
                          index?: number;
                        };
                        if (!value || value <= 0 || index == null) return null;
                        const row = chartData[index];
                        const isWholeActive =
                          row &&
                          activeFilter?.categoryKey === row.category_key &&
                          activeFilter.onDemand === null;
                        return (
                          <text
                            x={(x ?? 0) + (width ?? 0) / 2}
                            y={(y ?? 0) - 4}
                            textAnchor="middle"
                            fill={isWholeActive ? '#0e6a9a' : '#6b7280'}
                            fontSize={9}
                            fontWeight={600}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (row) onCategoryClick(row.category_key);
                            }}
                          >
                            <title>
                              {row
                                ? `${row.label} · ${t('inventoryPolicies.recommendations.chart.wholeCategoryHint')}`
                                : ''}
                            </title>
                            {`${value}%`}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <CategorySidebar
            rows={chartData}
            activeFilter={activeFilter}
            onSegmentClick={onSegmentClick}
            onCategoryClick={onCategoryClick}
            onClearFilter={onClearFilter}
          />
        </div>
      )}
      </div>

      <div className="mt-3 flex flex-col gap-1 border-t border-gray-100 pt-3 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <p className="text-xs text-gray-500">
          {t('inventoryPolicies.recommendations.chart.footnote')}
        </p>
        {totalRefs > 0 ? (
          <p className="text-xs font-medium tabular-nums text-gray-700">
            {t('inventoryPolicies.recommendations.chart.total', { count: totalRefs })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
